import axios, { AxiosRequestConfig } from 'axios'
import {
  Account,
  AccountActions,
  AccountManager,
  LdapImportResult,
  Action,
  App,
  ApiInfo,
  AuthenticationToken,
  Building,
  BusinessUnit,
  Company,
  Credentials,
  CvValue,
  CvType,
  Resource,
  AccountResource,
  FieldMetadataAccount,
  EditorialProject,
  Element,
  ElementPatch,
  ElementType,
  NewElement,
  UpsertProduct,
  ProductBulkRow,
  ProductBulkRowResult,
  Product,
  Project,
  SearchOptions,
  SearchProductOptions,
  SearchProjectOptions,
  SearchResult,
  SearchElementResult,
  SearchAccountsResult,
  SearchAccountNavigationResult,
  SearchAuditResult,
  SearchTeamMemberOptions,
  Service,
  TeamMember,
  Template,
  UpdateProduct,
  User,
  SearchElementOptions,
  ElementPostCopy,
  ElementPostMove,
  Audit,
  AuditOperationType,
  FileExport,
  ApiResponsePlain,
  ElementStatus,
  ElementStatusType,
  ElementStatusPhase,
  RegenerateWebpack,
  RegenerateWebpackResponse,
  RegenerateStaticNodesPost,
  RegenerateProductCodesPost,
  Milestone,
  SearchMilestoneOptions,
  SearchMilestoneResult,
  MilestoneDate,
  ProductPostCopyStructure,
  Permission,
  Provider,
  PrepArea,
  PrepTask,
  ProductionPrep,
  ProductionPrepArea,
  ProductionPrepTask,
  ProductionPrepTaskLog,
  PrepTaskStatus,
  Team,
  AccountTeam,
  AccountView,
  AccountViewType,
  AccountFilters,
  CopyProcessedRelatedPartsResponse
} from '@/models'
import { ApiResponse } from './api-response'
import store from '@/store'
import router from '@/router'
import { ActionTypes } from '@/store/action-types'

// Add a response interceptor
axios.interceptors.response.use(
  (response) => {
    return Promise.resolve(response)
  },
  function(error) {
    // Unauthorized - No auth token header:
    if (error.response && error.response.status === 401) {
      if (router.currentRoute.name !== 'Login') {
        store.dispatch(ActionTypes.AUTH_LOGOUT)
      }
    }
    return Promise.reject(error)
  }
)

function requestConfig(
  params: object = {},
  forceRefresh = false
): AxiosRequestConfig {
  const token = store.state.token
  let cacheHeaders = {}
  if (forceRefresh) {
    cacheHeaders = {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: 0
    }
  }

  return {
    headers: {
      Authorization: `${token.tokenType} ${token.accessToken}`,
      ...cacheHeaders
    },
    params: params
  }
}

export default class ApiClient {
  private static url = process.env.VUE_APP_CCP_API_URL
  private static urlDispatcher = process.env.VUE_APP_DISPATCHER_API_URL
  private static urlTeleport = process.env.VUE_APP_TELEPORT_API_URL
  private static apiInfoInitialized = false

  private static apiInfo: ApiInfo = {
    version: '--',
    commitHash: '--'
  }

  static async getInfo(): Promise<ApiInfo> {
    if (!this.apiInfoInitialized) {
      const resp = await axios.get(`${this.url}/info`)
      const respData: ApiResponse = resp.data
      this.apiInfo = respData.data as ApiInfo
      this.apiInfoInitialized = true
    }

    return this.apiInfo
  }

  static async login(credentials: Credentials): Promise<AuthenticationToken> {
    const resp = await axios
      .post(`${this.url}/auth/login`, credentials)
      .catch((err) => {
        return Promise.reject(err)
      })
    const respData: ApiResponse = resp.data
    return respData.data as AuthenticationToken
  }

  static async logout(): Promise<void> {
    try {
      return await axios.get(`${this.url}/auth/logout`, requestConfig())
    } catch (error: any) {
      return error
    }
  }

  static async getUserInfo(): Promise<User> {
    const resp = await axios.get(`${this.url}/users/me`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as User
  }

  static async searchAccounts(
    search = '',
    limit = 100,
    offset = 0,
    profile: string | null = null,
    country: string | null = null,
    businessUnit: string | null = null,
    active: boolean | null = null,
    editorialStatus: string | null = null,
    companyId: string | null = null
  ): Promise<SearchAccountsResult> {
    const params: any = {
      search,
      limit,
      offset
    }
    if (profile) params.profile = profile
    if (country) params.country = country
    if (businessUnit) params.businessUnit = businessUnit
    if (active !== null) params.active = active
    if (editorialStatus !== null) params.editorialStatus = editorialStatus
    if (companyId) params.companyId = companyId

    const resp = await axios.get(`${this.url}/accounts`, requestConfig(params))
    const respData: ApiResponse = resp.data
    return respData.data as SearchAccountsResult
  }

  static async getAccount(accountId: string): Promise<Account> {
    const resp = await axios.get(
      `${this.url}/accounts/${accountId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Account
  }

  static async createAccount(account: Account): Promise<Account> {
    const resp = await axios.post(
      `${this.url}/accounts`,
      account,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Account
    } else {
      throw Error(respData.message)
    }
  }

  static async updateAccount(
    accountId: string,
    account: Account
  ): Promise<Account> {
    const resp = await axios.put(
      `${this.url}/accounts/${accountId}`,
      account,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Account
    } else {
      throw Error(respData.message)
    }
  }

  static async getAccountProfiles(): Promise<string[]> {
    const resp = await axios.get(
      `${this.url}/accounts/profiles`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as string[]
  }

  static async registerNavigation(appId: string, url: string): Promise<void> {
    try {
      await axios.post(
        `${this.url}/navigation`,
        { appId, url },
        { ...requestConfig(), timeout: 5000 }
      )
    } catch (error) {
      console.debug('Navigation tracking failed:', error)
    }
  }

  static async getAccountNavigation(
    accountId: string,
    appId?: string,
    limit = 15,
    offset = 0
  ): Promise<SearchAccountNavigationResult> {
    const params = new URLSearchParams()
    params.append('accountId', accountId)
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    if (appId) {
      params.append('appId', appId)
    }

    const resp = await axios.get(
      `${this.url}/navigation?${params.toString()}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as SearchAccountNavigationResult
  }

  static async createPermission(permission: Permission): Promise<Permission> {
    const resp = await axios.post(
      `${this.url}/permissions`,
      permission,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Permission
    } else {
      throw Error(respData.message)
    }
  }

  static async updatePermission(
    permissionId: number,
    permission: Permission
  ): Promise<Permission> {
    const resp = await axios.patch(
      `${this.url}/permissions/${permissionId}`,
      permission,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Permission
    } else {
      throw Error(respData.message)
    }
  }

  static async deletePermission(permissionId: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/permissions/${permissionId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async copyPermissions(
    targetAccountId: string,
    originAccountId: string
  ): Promise<Account> {
    const resp = await axios.post(
      `${this.url}/accounts/${targetAccountId}/copy-permissions`,
      { accountOriginId: originAccountId },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Account
    } else {
      throw Error(respData.message)
    }
  }

  static async mergeAccounts(
    masterId: string,
    alternateIds: string[]
  ): Promise<Account> {
    const resp = await axios.post(
      `${this.url}/accounts/merge`,
      { masterId, alternateIds },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Account
    } else {
      throw Error(respData.message)
    }
  }

  static async importLdapAccounts(emails: string[]): Promise<LdapImportResult[]> {
    const resp = await axios.post(
      `${this.url}/accounts/import-ldap`,
      { emails },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as LdapImportResult[]
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteAccount(accountId: string): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/accounts/${accountId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async getAccountViews(): Promise<AccountView[]> {
    const resp = await axios.get(`${this.url}/account-views`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as AccountView[]
  }

  static async saveAccountView(
    name: string,
    type: AccountViewType,
    definition: { filters?: AccountFilters; columns?: string[] }
  ): Promise<AccountView> {
    const resp = await axios.post(
      `${this.url}/account-views`,
      { name, type, definition },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as AccountView
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteAccountView(id: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/account-views/${id}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async setAccountViewDefault(id: number, isDefault: boolean): Promise<AccountView> {
    const resp = await axios.put(
      `${this.url}/account-views/${id}/default`,
      { isDefault },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as AccountView
    } else {
      throw Error(respData.message)
    }
  }

  static async getApps(): Promise<App[]> {
    const resp = await axios.get(`${this.url}/apps`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as App[]
  }

  static async createApp(app: App): Promise<App> {
    const resp = await axios.post(`${this.url}/apps`, app, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as App
    } else {
      throw Error(respData.message)
    }
  }

  static async updateApp(appId: string, app: App): Promise<App> {
    const resp = await axios.patch(`${this.url}/apps/${appId}`, app, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as App
    } else {
      throw Error(respData.message)
    }
  }

  static async getActions(): Promise<Action[]> {
    const resp = await axios.get(`${this.url}/actions`, requestConfig())
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
    return respData.data as Action[]
  }

  static async createAccountAction(
    accountId: string,
    actionsId: string
  ): Promise<AccountActions> {
    const params = new URLSearchParams()
    params.append('accountId', accountId)
    params.append('actionsId', actionsId)

    const resp = await axios.post(
      `${this.url}/account-actions`,
      params,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
    return respData.data as AccountActions
  }

  static async deleteAccountAction(accountActionsId: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/account-actions/${accountActionsId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async createAccountManager(
    accountId: string,
    managerId: string
  ): Promise<AccountManager> {
    const params = new URLSearchParams()
    params.append('accountId', accountId)
    params.append('managerId', managerId)

    const resp = await axios.post(
      `${this.url}/account-manager`,
      params,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
    return respData.data as AccountManager
  }

  static async deleteAccountManager(accountManagerId: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/account-manager/${accountManagerId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async searchProducts(options: SearchProductOptions): Promise<any> {
    const resp = await axios.get(`${this.url}/products`, requestConfig(options))

    if (!options.export) {
      const respData: ApiResponse = resp.data
      return respData.data as SearchResult
    } else {
      const respData: ApiResponse = resp.data
      if (respData.success) {
        return respData.data as FileExport
      } else {
        throw Error(respData.message)
      }
    }
  }

  static async createProduct(product: UpsertProduct): Promise<Product> {
    const resp = await axios.post(
      `${this.url}/products`,
      product,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Product
    } else {
      throw Error(respData.message)
    }
  }

  /**
   * Creates the products one by one (optionally copying a source structure) and returns one
   * result per row; with dryRun the rows are only validated.
   */
  static async createProductsBulk(products: ProductBulkRow[], dryRun = false): Promise<ProductBulkRowResult[]> {
    const resp = await axios.post(
      `${this.url}/products/bulk`,
      { products, dryRun },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as ProductBulkRowResult[]
    } else {
      throw Error(respData.message)
    }
  }

  /** Next free code of the model products series (600XXX) */
  static async getNextModelProductCode(): Promise<string> {
    return this.unwrap(axios.get(`${this.url}/products/next-model-code`, requestConfig()))
  }

  static async putProduct(product: UpsertProduct): Promise<Product> {
    const resp = await axios.put(
      `${this.url}/products/${product.code}`,
      product,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Product
    } else {
      throw Error(respData.message)
    }
  }

  static async updateProduct(
    productCode: string,
    payload: UpdateProduct
  ): Promise<Product> {
    const resp = await axios.patch(
      `${this.url}/products/${productCode}`,
      payload,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Product
    } else {
      throw Error(respData.message)
    }
  }

  static async getProduct(productCode: string): Promise<Product> {
    const resp = await axios.get(
      `${this.url}/products/${productCode}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Product
    } else {
      throw Error(respData.message)
    }
  }

  static async getProductElements(productCode: string): Promise<Element[]> {
    const resp = await axios.get(
      `${this.url}/products/${productCode}/elements`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Element[]
  }

  static async regenerateWebpack(
    payload: RegenerateWebpack
  ): Promise<RegenerateWebpackResponse> {
    const resp = await axios.post(
      `${this.urlDispatcher}/webpack/build`,
      payload,
      requestConfig()
    )
    return resp.data as RegenerateWebpackResponse
  }

  static async getElement(elementId: string): Promise<Element> {
    const resp = await axios.get(
      `${this.url}/elements/${elementId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Element
  }

  static async createElement(
    element: NewElement,
    productCode: string,
    parentElementId = ''
  ): Promise<Element> {
    const params = {
      parentId: parentElementId
    }

    const resp = await axios.post(
      `${this.url}/products/${productCode}/elements`,
      element,
      requestConfig(params)
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Element
    } else {
      throw Error(respData.message)
    }
  }

  static async updateElement(
    element: ElementPatch,
    elementId: string
  ): Promise<Element> {
    const resp = await axios.patch(
      `${this.url}/elements/${elementId}`,
      element,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Element
    } else {
      throw Error(respData.message)
    }
  }

  static async updateElementName(
    elementId: string,
    name: string
  ): Promise<Element> {
    const resp = await axios.patch(
      `${this.url}/elements/${elementId}/name`,
      { name },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Element
    } else {
      throw Error(respData.message)
    }
  }

  static async copyElement(
    element: ElementPostCopy,
    elementId: string
  ): Promise<Element> {
    const resp = await axios.post(
      `${this.url}/elements/${elementId}/copy`,
      element,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Element
    } else {
      throw Error(respData.message)
    }
  }

  static async copyAllStructure(
    payload: ProductPostCopyStructure
  ): Promise<any> {
    const resp = await axios.post(
      `${this.url}/products/${payload.productCodeTarget}/copy-structure`,
      payload,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data
    } else {
      throw Error(respData.message)
    }
  }

  static async moveElement(
    element: ElementPostMove,
    elementId: string
  ): Promise<Element> {
    const resp = await axios.post(
      `${this.url}/elements/${elementId}/move`,
      element,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Element
    } else {
      throw Error(respData.message)
    }
  }

  static async generateDigital(elementId: string): Promise<Element> {
    const resp = await axios.post(
      `${this.url}/elements/${elementId}/generate-digital`,
      {},
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Element
    } else {
      throw Error(respData.message)
    }
  }

  static async exportStructureElement(
    elementId: string,
    typeExcel: string
  ): Promise<any> {
    const resp = await axios.get(
      `${this.url}/elements/${elementId}/export`,
      requestConfig({ typeExcel: typeExcel })
    )

    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as FileExport
    } else {
      throw Error(respData.message)
    }
  }

  static async searchElements(
    options: SearchElementOptions
  ): Promise<SearchElementResult> {
    const resp = await axios.get(`${this.url}/elements`, requestConfig(options))
    const respData: ApiResponse = resp.data
    return respData.data as SearchElementResult
  }

  static async searchAudit(
    primaryKey: string,
    modelKt: string
  ): Promise<Audit[]> {
    const options = {
      primaryKey: primaryKey,
      modelKt: modelKt
    }
    const resp = await axios.get(`${this.url}/audits`, requestConfig(options))
    const respData: ApiResponse = resp.data
    return respData.data as Audit[]
  }

  static async getAuditOperationTypes(): Promise<AuditOperationType[]> {
    const resp = await axios.get(
      `${this.url}/audits/operation-types`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as AuditOperationType[]
  }

  static async searchAccountAudit(
    accountId: string,
    offset: number,
    limit: number,
    modelKt: string | null = null,
    operationType: string | null = null,
    userId: string | null = null
  ): Promise<SearchAuditResult> {
    const params: any = { offset, limit }
    if (modelKt && modelKt !== 'all') {
      params.modelKt = modelKt
    }
    if (operationType && operationType !== 'all') {
      params.operationType = operationType
    }
    if (userId && userId.trim()) {
      params.userId = userId.trim()
    }

    const resp = await axios.get(
      `${this.url}/audits/account/${accountId}`,
      requestConfig(params)
    )
    const respData: ApiResponse = resp.data
    return respData.data as SearchAuditResult
  }

  static async deleteProduct(productCode: string): Promise<Product> {
    const resp = await axios.delete(
      `${this.url}/products/${productCode}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Product
  }

  static async regenerateElementCodesInProduct(
    productCode: string
  ): Promise<boolean> {
    const resp = await axios.post(
      `${this.url}/products/${productCode}/regenerate-element-codes`,
      {},
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.success
  }

  static async generateSampleProduct(productCode: string): Promise<boolean> {
    const resp = await axios.post(
      `${this.url}/products/${productCode}/generate-sample`,
      {},
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.success
  }

  static async regenerateStaticNodes(productCode: string): Promise<boolean> {
    const resp = await axios.post(
      `${this.url}/products/${productCode}/generate-static-nodes`,
      {},
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.success
  }

  static async regenerateStaticNodesGlobal(
    payload: RegenerateStaticNodesPost
  ): Promise<boolean> {
    const resp = await axios.post(
      `${this.url}/tools/regenerate-static-nodes`,
      payload,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.success
  }

  static async regenerateProductCodesGlobal(
    payload: RegenerateProductCodesPost
  ): Promise<boolean> {
    const resp = await axios.post(
      `${this.url}/tools/regenerate-product-codes`,
      payload,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.success
  }

  static async importECFromSharepoint(productCode: string): Promise<boolean> {
    const resp = await axios.post(
      `${this.url}/products/${productCode}/structure/import`,
      {},
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.success
  }

  static async importECFromExcel(
    productCode: string,
    elementId: string,
    file: File
  ): Promise<boolean> {
    const params = {
      elementId: elementId
    }
    const fileData = new FormData()
    fileData.append('ec-file', file)

    const resp = await axios.post(
      `${this.url}/products/${productCode}/structure/upload`,
      fileData,
      requestConfig(params)
    )
    const respData: ApiResponse = resp.data
    return respData.success
  }

  static async deleteElement(elementId: string): Promise<Element> {
    const resp = await axios.delete(
      `${this.url}/elements/${elementId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Element
  }

  static async getServices(): Promise<Service[]> {
    const resp = await axios.get(`${this.url}/services`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as Service[]
  }

  static async getTemplates(): Promise<Template[]> {
    const resp = await axios.get(`${this.url}/nodes-templates`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as Template[]
  }

  static async createTemplate(template: Template): Promise<Template> {
    const resp = await axios.post(
      `${this.url}/nodes-templates`,
      template,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Template
    } else {
      throw Error(respData.message)
    }
  }

  static async updateTemplate(
    templateId: number,
    template: Template
  ): Promise<Template> {
    const resp = await axios.patch(
      `${this.url}/nodes-templates/${templateId}`,
      template,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Template
    } else {
      throw Error(respData.message)
    }
  }

  static async getTeamMembers(
    options: SearchTeamMemberOptions
  ): Promise<TeamMember[]> {
    const resp = await axios.get(
      `${this.url}/team-members`,
      requestConfig(options)
    )
    const respData: ApiResponse = resp.data
    return respData.data as TeamMember[]
  }

  /** includeProductionPrep adds the progress of each area of the project preparation */
  static async getEditorialProjects(includeProductionPrep = false): Promise<EditorialProject[]> {
    const resp = await axios.get(
      `${this.url}/editorial-projects`,
      requestConfig(includeProductionPrep ? { includeProductionPrep: true } : {})
    )
    const respData: ApiResponse = resp.data
    return respData.data as EditorialProject[]
  }

  static async createEditorialProject(
    editorialProject: EditorialProject
  ): Promise<EditorialProject> {
    const resp = await axios.post(
      `${this.url}/editorial-projects`,
      editorialProject,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as EditorialProject
    } else {
      throw Error(respData.message)
    }
  }

  static async updateEditorialProject(
    editorialProjectCode: string,
    editorialProject: EditorialProject
  ): Promise<EditorialProject> {
    const resp = await axios.patch(
      `${this.url}/editorial-projects/${editorialProjectCode}`,
      editorialProject,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as EditorialProject
    } else {
      throw Error(respData.message)
    }
  }

  static async getDevelopmentManagers(): Promise<string[]> {
    const resp = await axios.get(
      `${this.url}/development-managers`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as string[]
  }

  static async getProject(projectCode: string): Promise<Project> {
    const resp = await axios.get(
      `${this.url}/projects/${projectCode}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Project
  }

  static async searchProjects(
    options: SearchProjectOptions
  ): Promise<SearchResult> {
    const resp = await axios.get(`${this.url}/projects`, requestConfig(options))
    const respData: ApiResponse = resp.data
    return respData.data as SearchResult
  }

  static async getProjectServices(projectCode: string): Promise<Service[]> {
    const resp = await axios.get(
      `${this.url}/projects/${projectCode}/services`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Service[]
  }

  static async getProductServices(productCode: string): Promise<Service[]> {
    const resp = await axios.get(
      `${this.url}/products/${productCode}/services`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Service[]
  }

  static async getProjectProducts(
    projectCode: string,
    options: SearchOptions
  ): Promise<SearchResult> {
    const resp = await axios.get(
      `${this.url}/projects/${projectCode}/products`,
      requestConfig(options)
    )
    const respData: ApiResponse = resp.data
    return respData.data as SearchResult
  }

  // The CV GET endpoints send a long-lived Cache-Control, so `noCache` appends a cache-busting
  // param to force a fresh read after a mutation (used by the admin management view).
  static async getCvValuesByType(type: string, noCache = false): Promise<CvValue[]> {
    const bust = noCache ? `&_=${Date.now()}` : ''
    const resp = await axios.get(
      `${this.url}/controlled-vocabulary?typeId=` + type + bust,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as CvValue[]
  }

  static async getCvTypes(noCache = false): Promise<CvType[]> {
    const bust = noCache ? `?_=${Date.now()}` : ''
    const resp = await axios.get(`${this.url}/controlled-vocabulary/types` + bust, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as CvType[]
  }

  static async createCvType(type: CvType): Promise<CvType> {
    const resp = await axios.post(`${this.url}/controlled-vocabulary/types`, type, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as CvType
    } else {
      throw Error(respData.message)
    }
  }

  static async updateCvType(typeId: string, type: CvType): Promise<CvType> {
    const resp = await axios.patch(`${this.url}/controlled-vocabulary/types/${typeId}`, type, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as CvType
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteCvType(typeId: string): Promise<void> {
    const resp = await axios.delete(`${this.url}/controlled-vocabulary/types/${typeId}`, requestConfig())
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async createCvValue(value: CvValue): Promise<CvValue> {
    const resp = await axios.post(`${this.url}/controlled-vocabulary`, value, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as CvValue
    } else {
      throw Error(respData.message)
    }
  }

  static async updateCvValue(codeId: string, value: CvValue): Promise<CvValue> {
    const resp = await axios.patch(`${this.url}/controlled-vocabulary/${codeId}`, value, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as CvValue
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteCvValue(codeId: string): Promise<void> {
    const resp = await axios.delete(`${this.url}/controlled-vocabulary/${codeId}`, requestConfig())
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  // ------------------------------ Resources (catalog) ------------------------------

  static async getResources(): Promise<Resource[]> {
    const resp = await axios.get(`${this.url}/resources`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as Resource[]
  }

  static async createResource(resource: Resource): Promise<Resource> {
    const resp = await axios.post(`${this.url}/resources`, resource, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Resource
    } else {
      throw Error(respData.message)
    }
  }

  static async updateResource(id: number, resource: Resource): Promise<Resource> {
    const resp = await axios.patch(`${this.url}/resources/${id}`, resource, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Resource
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteResource(id: number): Promise<void> {
    const resp = await axios.delete(`${this.url}/resources/${id}`, requestConfig())
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  // ------------------------ Account resources (assignments) ------------------------

  static async getAccountResources(accountId: string): Promise<AccountResource[]> {
    const resp = await axios.get(`${this.url}/account-resources?accountId=${accountId}`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as AccountResource[]
  }

  static async createAccountResource(accountResource: AccountResource): Promise<AccountResource> {
    const resp = await axios.post(`${this.url}/account-resources`, accountResource, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as AccountResource
    } else {
      throw Error(respData.message)
    }
  }

  static async updateAccountResource(id: number, accountResource: AccountResource): Promise<AccountResource> {
    const resp = await axios.patch(`${this.url}/account-resources/${id}`, accountResource, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as AccountResource
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteAccountResource(id: number): Promise<void> {
    const resp = await axios.delete(`${this.url}/account-resources/${id}`, requestConfig())
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async getFieldMetadataByCompany(companyId: string): Promise<FieldMetadataAccount[]> {
    const resp = await axios.get(
      `${this.url}/field-metadata-account/company/${companyId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as FieldMetadataAccount[]
    } else {
      throw Error(respData.message)
    }
  }

  static async createFieldMetadataAccount(field: FieldMetadataAccount): Promise<FieldMetadataAccount> {
    const resp = await axios.post(`${this.url}/field-metadata-account`, field, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as FieldMetadataAccount
    } else {
      throw Error(respData.message)
    }
  }

  static async updateFieldMetadataAccount(id: number, field: FieldMetadataAccount): Promise<FieldMetadataAccount> {
    const resp = await axios.patch(`${this.url}/field-metadata-account/${id}`, field, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as FieldMetadataAccount
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteFieldMetadataAccount(id: number): Promise<void> {
    const resp = await axios.delete(`${this.url}/field-metadata-account/${id}`, requestConfig())
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async getElementTypes(): Promise<ElementType[]> {
    const resp = await axios.get(`${this.url}/element-types`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as ElementType[]
  }

  static async createElementType(
    elementType: ElementType
  ): Promise<ElementType> {
    const resp = await axios.post(
      `${this.url}/element-types`,
      elementType,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as ElementType
    } else {
      throw Error(respData.message)
    }
  }

  static async updateElementType(
    elementTypeCode: string,
    elementType: ElementType
  ): Promise<ElementType> {
    const resp = await axios.patch(
      `${this.url}/element-types/${elementTypeCode}`,
      elementType,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as ElementType
    } else {
      throw Error(respData.message)
    }
  }

  static async searchMilestones(
    options: SearchMilestoneOptions
  ): Promise<SearchMilestoneResult> {
    const resp = await axios.get(
      `${this.url}/milestones`,
      requestConfig(options)
    )
    const respData: ApiResponse = resp.data
    return respData.data as SearchMilestoneResult
  }

  static async createMilestone(milestone: Milestone): Promise<Milestone> {
    const resp = await axios.post(
      `${this.url}/milestones`,
      milestone,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Milestone
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteMilestone(milestoneId: number): Promise<Milestone> {
    const resp = await axios.delete(
      `${this.url}/milestones/${milestoneId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Milestone
  }

  static async createMilestoneDate(
    milestoneDate: MilestoneDate
  ): Promise<MilestoneDate> {
    const resp = await axios.post(
      `${this.url}/milestone-dates`,
      milestoneDate,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as MilestoneDate
    } else {
      throw Error(respData.message)
    }
  }

  static async updateMilestoneDate(
    milestoneDate: MilestoneDate
  ): Promise<MilestoneDate> {
    const resp = await axios.put(
      `${this.url}/milestone-dates/${milestoneDate.milestoneDateId}`,
      milestoneDate,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as MilestoneDate
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteMilestoneDate(
    milestoneDateId: number
  ): Promise<MilestoneDate> {
    const resp = await axios.delete(
      `${this.url}/milestone-dates/${milestoneDateId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as MilestoneDate
  }

  static async getElementStatus(codSap: string): Promise<ElementStatus[]> {
    const options: any = { codSap: codSap }
    const resp = await axios.get(
      `${this.url}/element-status`,
      requestConfig(options)
    )
    const respData: ApiResponse = resp.data
    return respData.data as ElementStatus[]
  }

  static async getElementStatusType(): Promise<ElementStatusType[]> {
    const resp = await axios.get(
      `${this.url}/element-status-type`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as ElementStatusType[]
  }

  static async getElementStatusPhase(): Promise<ElementStatusPhase[]> {
    const resp = await axios.get(
      `${this.url}/element-status-phase`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as ElementStatusPhase[]
  }

  static async importExcelTraductions(
    productCode: string,
    file: File
  ): Promise<ApiResponsePlain> {
    const fileData = new FormData()
    fileData.append('excel-file', file)

    const resp = await axios.post(
      `${this.url}/products/${productCode}/elements/import`,
      fileData,
      requestConfig()
    )
    const respData: ApiResponsePlain = resp.data
    return respData
  }

  static async copyProcessedRelatedPartsPdfs(
    productCode: string
  ): Promise<CopyProcessedRelatedPartsResponse> {
    const resp = await axios.post(
      `${this.urlTeleport}/ccp/relation`,
      { codSap: productCode, dryRun: false },
      requestConfig()
    )
    return resp.data as CopyProcessedRelatedPartsResponse
  }

  static async getCompanies(): Promise<Company[]> {
    const resp = await axios.get(`${this.url}/companies`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as Company[]
  }

  static async getCompany(companyId: string): Promise<Company> {
    const resp = await axios.get(
      `${this.url}/companies/${companyId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Company
    } else {
      throw Error(respData.message)
    }
  }

  static async createCompany(company: Company): Promise<Company> {
    const resp = await axios.post(
      `${this.url}/companies`,
      company,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Company
    } else {
      throw Error(respData.message)
    }
  }

  static async updateCompany(
    companyId: string,
    company: Company
  ): Promise<Company> {
    const resp = await axios.patch(
      `${this.url}/companies/${companyId}`,
      company,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Company
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteCompany(companyId: string): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/companies/${companyId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async getBusinessUnits(): Promise<BusinessUnit[]> {
    const resp = await axios.get(`${this.url}/business-units`, requestConfig())
    const respData: ApiResponse = resp.data
    return respData.data as BusinessUnit[]
  }

  static async getBusinessUnit(businessUnitId: string): Promise<BusinessUnit> {
    const resp = await axios.get(
      `${this.url}/business-units/${businessUnitId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as BusinessUnit
    } else {
      throw Error(respData.message)
    }
  }

  static async createBusinessUnit(businessUnit: BusinessUnit): Promise<BusinessUnit> {
    const resp = await axios.post(
      `${this.url}/business-units`,
      businessUnit,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as BusinessUnit
    } else {
      throw Error(respData.message)
    }
  }

  static async updateBusinessUnit(
    businessUnitId: string,
    businessUnit: BusinessUnit
  ): Promise<BusinessUnit> {
    const resp = await axios.patch(
      `${this.url}/business-units/${businessUnitId}`,
      businessUnit,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as BusinessUnit
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteBusinessUnit(businessUnitId: string): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/business-units/${businessUnitId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async getBuildings(): Promise<Building[]> {
    const resp = await axios.get(
      `${this.url}/buildings`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    return respData.data as Building[]
  }

  static async getBuilding(buildingId: number): Promise<Building> {
    const resp = await axios.get(
      `${this.url}/buildings/${buildingId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Building
    } else {
      throw Error(respData.message)
    }
  }

  static async createBuilding(building: Building): Promise<Building> {
    const resp = await axios.post(
      `${this.url}/buildings`,
      building,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Building
    } else {
      throw Error(respData.message)
    }
  }

  static async updateBuilding(
    buildingId: number,
    building: Building
  ): Promise<Building> {
    const resp = await axios.patch(
      `${this.url}/buildings/${buildingId}`,
      building,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Building
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteBuilding(buildingId: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/buildings/${buildingId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async getTeams(responsibleId?: string): Promise<Team[]> {
    const params: any = {}
    if (responsibleId) params.responsibleId = responsibleId
    const resp = await axios.get(`${this.url}/teams`, requestConfig(params))
    const respData: ApiResponse = resp.data
    return respData.data as Team[]
  }

  static async getTeam(teamId: number): Promise<Team> {
    const resp = await axios.get(`${this.url}/teams/${teamId}`, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Team
    } else {
      throw Error(respData.message)
    }
  }

  static async createTeam(team: Team): Promise<Team> {
    const resp = await axios.post(`${this.url}/teams`, team, requestConfig())
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Team
    } else {
      throw Error(respData.message)
    }
  }

  static async updateTeam(teamId: number, team: Team): Promise<Team> {
    const resp = await axios.patch(
      `${this.url}/teams/${teamId}`,
      team,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Team
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteTeam(teamId: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/teams/${teamId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async createAccountTeam(
    accountId: string,
    teamId: number
  ): Promise<AccountTeam> {
    const resp = await axios.post(
      `${this.url}/account-teams`,
      { accountId, teamId },
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as AccountTeam
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteAccountTeam(accountTeamId: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/account-teams/${accountTeamId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  static async getProviders(responsibleId?: string): Promise<Provider[]> {
    const params: any = {}
    if (responsibleId) params.responsibleId = responsibleId
    const resp = await axios.get(`${this.url}/providers`, requestConfig(params))
    const respData: ApiResponse = resp.data
    return respData.data as Provider[]
  }

  static async getProvider(providerId: number): Promise<Provider> {
    const resp = await axios.get(
      `${this.url}/providers/${providerId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Provider
    } else {
      throw Error(respData.message)
    }
  }

  static async createProvider(provider: Provider): Promise<Provider> {
    const resp = await axios.post(
      `${this.url}/providers`,
      provider,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Provider
    } else {
      throw Error(respData.message)
    }
  }

  static async updateProvider(
    providerId: number,
    provider: Provider
  ): Promise<Provider> {
    const resp = await axios.patch(
      `${this.url}/providers/${providerId}`,
      provider,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (respData.success) {
      return respData.data as Provider
    } else {
      throw Error(respData.message)
    }
  }

  static async deleteProvider(providerId: number): Promise<void> {
    const resp = await axios.delete(
      `${this.url}/providers/${providerId}`,
      requestConfig()
    )
    const respData: ApiResponse = resp.data
    if (!respData.success) {
      throw Error(respData.message)
    }
  }

  // Production preparation: catalog and preparations (CCP ADMIN role only)

  /** Data of the API response, or its message as the error */
  private static async unwrap<T>(request: Promise<{ data: ApiResponse }>): Promise<T> {
    const respData: ApiResponse = (await request).data
    if (respData.success) {
      return respData.data as unknown as T
    } else {
      throw Error(respData.message)
    }
  }

  static async getPrepCatalog(): Promise<PrepArea[]> {
    return this.unwrap(axios.get(`${this.url}/prep-catalog`, requestConfig()))
  }

  static async savePrepArea(area: PrepArea): Promise<PrepArea> {
    return this.unwrap(area.id
      ? axios.patch(`${this.url}/prep-catalog/areas/${area.id}`, area, requestConfig())
      : axios.post(`${this.url}/prep-catalog/areas`, area, requestConfig()))
  }

  static async deletePrepArea(areaId: number): Promise<void> {
    return this.unwrap(axios.delete(`${this.url}/prep-catalog/areas/${areaId}`, requestConfig()))
  }

  static async savePrepTask(task: PrepTask): Promise<PrepTask> {
    return this.unwrap(task.id
      ? axios.patch(`${this.url}/prep-catalog/tasks/${task.id}`, task, requestConfig())
      : axios.post(`${this.url}/prep-catalog/tasks`, task, requestConfig()))
  }

  static async deletePrepTask(taskId: number): Promise<void> {
    return this.unwrap(axios.delete(`${this.url}/prep-catalog/tasks/${taskId}`, requestConfig()))
  }

  static async getProductionPreps(): Promise<ProductionPrep[]> {
    return this.unwrap(axios.get(`${this.url}/production-preps`, requestConfig()))
  }

  static async getProductionPrep(prepId: number): Promise<ProductionPrep> {
    return this.unwrap(axios.get(`${this.url}/production-preps/${prepId}`, requestConfig()))
  }

  static async saveProductionPrep(prep: ProductionPrep): Promise<ProductionPrep> {
    return this.unwrap(prep.id
      ? axios.patch(`${this.url}/production-preps/${prep.id}`, prep, requestConfig())
      : axios.post(`${this.url}/production-preps`, prep, requestConfig()))
  }

  static async deleteProductionPrep(prepId: number): Promise<void> {
    return this.unwrap(axios.delete(`${this.url}/production-preps/${prepId}`, requestConfig()))
  }

  static async saveProductionPrepArea(prepId: number, area: ProductionPrepArea): Promise<ProductionPrepArea> {
    return this.unwrap(area.id
      ? axios.patch(`${this.url}/production-preps/${prepId}/areas/${area.id}`, area, requestConfig())
      : axios.post(`${this.url}/production-preps/${prepId}/areas`, area, requestConfig()))
  }

  static async deleteProductionPrepArea(prepId: number, areaId: number): Promise<void> {
    return this.unwrap(axios.delete(`${this.url}/production-preps/${prepId}/areas/${areaId}`, requestConfig()))
  }

  static async saveProductionPrepTask(prepId: number, task: ProductionPrepTask): Promise<ProductionPrepTask> {
    return this.unwrap(task.id
      ? axios.patch(`${this.url}/production-preps/${prepId}/tasks/${task.id}`, task, requestConfig())
      : axios.post(`${this.url}/production-preps/${prepId}/tasks`, task, requestConfig()))
  }

  static async deleteProductionPrepTask(prepId: number, taskId: number): Promise<void> {
    return this.unwrap(axios.delete(`${this.url}/production-preps/${prepId}/tasks/${taskId}`, requestConfig()))
  }

  static async updateProductionPrepTaskStatus(
    prepId: number,
    taskId: number,
    status: PrepTaskStatus,
    notes?: string | null
  ): Promise<ProductionPrepTask> {
    return this.unwrap(axios.put(
      `${this.url}/production-preps/${prepId}/tasks/${taskId}/status`, { status, notes }, requestConfig()
    ))
  }

  /** Gives the status to every task of the area; answers the area with its tasks */
  static async updateProductionPrepAreaStatus(prepId: number, areaId: number, status: PrepTaskStatus): Promise<ProductionPrepArea> {
    return this.unwrap(axios.put(`${this.url}/production-preps/${prepId}/areas/${areaId}/status`, { status }, requestConfig()))
  }

  /** Keys of the automatic checks a task can have */
  static async getPrepAutoChecks(): Promise<string[]> {
    return this.unwrap(axios.get(`${this.url}/prep-catalog/auto-checks`, requestConfig()))
  }

  /** Runs the automatic checks of the preparation; answers the tasks that have one */
  static async runProductionPrepAutoChecks(prepId: number): Promise<ProductionPrepTask[]> {
    return this.unwrap(axios.post(`${this.url}/production-preps/${prepId}/auto-checks`, {}, requestConfig()))
  }

  static async runProductionPrepTaskAutoCheck(prepId: number, taskId: number): Promise<ProductionPrepTask> {
    return this.unwrap(axios.post(`${this.url}/production-preps/${prepId}/tasks/${taskId}/auto-check`, {}, requestConfig()))
  }

  static async getProductionPrepTaskLogs(prepId: number, taskId: number): Promise<ProductionPrepTaskLog[]> {
    return this.unwrap(axios.get(`${this.url}/production-preps/${prepId}/tasks/${taskId}/logs`, requestConfig()))
  }
}
